import { ProductType } from "@/commons/enums/ProductType";
import DictionaryStore from "@/commons/services/store/DictionaryStore";
import { I18NGetter } from "@/services/enumTranslator/I18NGetter";
import { ProductCategories, ProductCategory, ProductTypeFromDictionary, } from "@/commons/types/productCategories.types";
import { IMultiformDictionary } from "@/modules/multiForm/shared/IMultiformDictionary";
import { ProductsData } from "@/commons/dictionaries/financialInstitutions/FinancialInstitutions";
import { ProductCategoryEnum } from "@/commons/enums/ProductCategory";
import EnvironmentService from "@/env/EnvironmentService";

export const useProductCategories = (enabledCategories?: boolean) => {
    let categories: ProductCategories = DictionaryStore.getDictionary('productCategories')
        .filter((category) => {
            if (enabledCategories) {
                return category.productItems.length > 0 && !['investmentCategory', 'nonBankCategory'].includes(category.category)
            } else {
                return category.productItems.length > 0
            }
        })
        .sort((a, b) => {
            const order = {
                mortgageLoanCategory: 1,
                cashLoanCategory: 2,
                smeCategory: 3, 
                insuranceCategory: 4,
                realEstateCategory: 5,
                investmentCategory: 6,
                nonBankCategory: 7
            };
            return order[a.category] - order[b.category];
        });

        // Exclude categories / product types specific for NESTO_RO domain
        if (EnvironmentService.Environment.isAppDomain().NESTO_RO) {
            // Remove whole categories: Real Estate and Insurance
            categories = categories
                .filter((category) => category.category !== ProductCategoryEnum.REAL_ESTATE && category.category !== ProductCategoryEnum.INSURANCE)
                // From SME category remove Factoring product type
                .map((category) => {
                    if (category.category !== ProductCategoryEnum.SME) return category;
                    return {
                        ...category,
                        productItems: category.productItems.filter((item) => item.productType !== ProductType.FACTORING),
                    };
                });
        }

        const getEnabledCategories = () => {
            return categories.filter(category => !['investmentCategory', 'nonBankCategory'].includes(category.category))
        }


        const getProductCategory = (productType: ProductType): ProductCategoryEnum | undefined => {
            const foundCategory = categories.find(productCategory => 
                productCategory.productItems.some(productTypeConfig => productTypeConfig.productType === productType)
        );
        return foundCategory?.category;
    }

    const getAllProductTypesFromCategory = (category: ProductCategoryEnum): ProductTypeFromDictionary[] => {
        return categories.find((productCategory: ProductCategory) => {
            return productCategory.category === category
        })?.productItems! || []
    }

    const isProductTypeInCategory = (productType: ProductType, productCategoryToCheck: ProductCategoryEnum) => {
        return !!categories.find((productCategory: ProductCategory) => productCategory.category === productCategoryToCheck)?.productItems.some((productTypeConfig) => productTypeConfig.productType === productType)
    }

    const mapCategoryToProductTypeMultiformDictionary = (category: ProductCategoryEnum): IMultiformDictionary<ProductType> => {
       return getAllProductTypesFromCategory(category).map((productTypeConfig) => ({
            type: productTypeConfig.productType,
            name_pl: I18NGetter().ProductTypeShortNames[productTypeConfig.productType]
        })
    )
}

const getProductCategories = (): IMultiformDictionary<ProductCategoryEnum> => {
    return categories.map((category: ProductCategory) => ({type: category.category, name_pl: I18NGetter().ProductCategory[category.category] }))
}
const getCategoryForProductType = (productType: ProductType): ProductCategoryEnum => {
    const category = categories.find((productCategory: ProductCategory) => 
        productCategory.productItems.some(product => product.productType === productType)
    );
    if (!category) {
        console.log('no category found for product type', productType);
    }
    return category?.category!;
}


const getProductTypesFromCategories = (categoriesNames: ProductCategoryEnum[]): ProductTypeFromDictionary[] => {
    return categoriesNames.flatMap(value => getAllProductTypesFromCategory(value));
}

const reduceProductTypesToCategoriesWithProductTypesAsArray = (productTypes: ProductsData): Record<ProductCategoryEnum, ProductType[]> => {
    return Object.entries(productTypes).reduce((acc: Record<ProductCategoryEnum, ProductType[]>, [key, value]: [string, boolean]) => {
        if (!value) return acc;
        const category = getCategoryForProductType(key as ProductType);
        
        if (category) {
            const existingCategory = acc[category] || [];
            
            acc[category] = [...existingCategory, key as ProductType];
        }
        return acc;
    }, {} as Record<ProductCategoryEnum, ProductType[]> );
}


    return {
        getProductCategory,
        isProductTypeInCategory,
        getAllProductTypesFromCategory,
        categories,
        mapCategoryToProductTypeMultiformDictionary,
        getProductCategories,
        getProductTypesFromCategories,
        getCategoryForProductType,
        reduceProductTypesToCategoriesWithProductTypesAsArray,
        getEnabledCategories,
    }
}